import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { t } from "../../../../lib/i18n";
import { speak, speakEn } from "../../../../lib/speak";
import { Art } from "../../../../ui/Art";
import { Screen, StudyGate, useLang } from "../../kit";
import { GameHeader, Intro, Result, YearChips, useYear } from "../common";
import { loadRound, type Topic } from "./source";
import type { ArcadeQ, EngineCtx, EngineFactory, Hud } from "./types";

/* The page around every arcade game: choose mascot, year and topic, load the questions, run the engine, show the result. */

export interface ArcadeSpec {
  /** Stored game id (game_sessions.game). */
  id: string;
  title: string;
  tone: string;
  emoji: string;
  intro: string;
  /** Logical canvas size, used for the CSS aspect ratio. */
  w: number;
  h: number;
  load: () => Promise<EngineFactory>;
  /** Control hint under the game (defaults to the lane hint). */
  hint?: string;
  /** Show the 3D-driver picker. */
  mascot?: boolean;
  /** Topic chips for this game (defaults to Matematik / Sains / Campur). */
  topics?: { id: Topic; label: string }[];
  /** Label over the small HUD note next to the question (defaults to "Tempat"). */
  noteLabel?: string;
  /** Read each question aloud in its own language. */
  speak?: boolean;
}

const TOPICS: { id: Topic; label: string }[] = [
  { id: "mat", label: "Matematik" },
  { id: "sains", label: "Sains" },
  { id: "campur", label: "Campur" },
];

const reduced = () => { try { return window.matchMedia("(prefers-reduced-motion: reduce)").matches; } catch { return false; } };

function useStored<T extends string>(key: string, fallback: T): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => { try { return (localStorage.getItem(key) as T) || fallback; } catch { return fallback; } });
  return [v, (x) => { setV(x); try { localStorage.setItem(key, x); } catch { /* ignore */ } }];
}

export function ArcadePage({ spec }: { spec: ArcadeSpec }) {
  return <Screen><StudyGate><Arcade spec={spec} /></StudyGate></Screen>;
}

function Arcade({ spec }: { spec: ArcadeSpec }) {
  const { lang } = useLang();
  const [year, setYear] = useYear();
  const topics = spec.topics ?? TOPICS;
  const [storedTopic, setTopic] = useStored<Topic>(spec.topics ? `arcade.topic.${spec.id}` : "arcade.topic", topics[0]!.id);
  const topic = topics.some((x) => x.id === storedTopic) ? storedTopic : topics[0]!.id;
  const [mascot, setMascot] = useStored<"aiman" | "aina">("arcade.mascot", "aiman");
  const [stage, setStage] = useState<"intro" | "loading" | "play" | "end">("intro");
  const [qs, setQs] = useState<ArcadeQ[]>([]);
  const [factory, setFactory] = useState<EngineFactory | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hud, setHud] = useState<Hud>({ n: 0, total: 10, score: 0 });
  const [final, setFinal] = useState<{ score: number; max: number; details?: Record<string, string | number | boolean> } | null>(null);
  const [seed, setSeed] = useState(0);

  const header = <GameHeader title={t(spec.title)} emoji={spec.emoji} tone={spec.tone} score={stage === "play" ? hud.score : undefined} progress={stage === "play" ? hud.n / Math.max(1, hud.total) : undefined} />;

  async function start() {
    setStage("loading"); setError(null); setFinal(null);
    try {
      const [round, f] = await Promise.all([loadRound(topic, year, lang === "en" ? "en" : "ms", 10), spec.load()]);
      if (round.length < 4) throw new Error("empty");
      setQs(round); setFactory(() => f); setHud({ n: 0, total: round.length, score: 0 }); setSeed((x) => x + 1); setStage("play");
    } catch {
      setError(t("Tiada soalan untuk pilihan ini lagi.")); setStage("intro");
    }
  }

  if (stage === "intro" || stage === "loading") {
    return (
      <>
        {header}
        <Intro emoji={spec.emoji} title={t(spec.title)} text={t(spec.intro)} onStart={stage === "loading" ? () => undefined : start} startLabel={stage === "loading" ? t("Memuatkan…") : undefined}>
          {spec.mascot && (
            <div className="flex justify-center gap-3" role="group" aria-label={t("Pilih pemandu")}>
              {([["aiman", "mascot/boy-cheer", "Harraz"], ["aina", "mascot/girl-cheer", "Hannah"]] as const).map(([id, art, name]) => (
                <button key={id} type="button" aria-pressed={mascot === id} onClick={() => setMascot(id)}
                  className={`flex w-28 flex-col items-center rounded-2xl p-2 ring-2 transition ${mascot === id ? "bg-amber-50 ring-amber-400" : "bg-card ring-black/10"}`}>
                  <Art id={art} alt="" className="h-20 w-20" />
                  <span className="text-sm font-extrabold text-slate-800">{name}</span>
                </button>
              ))}
            </div>
          )}
          <YearChips year={year} onYear={setYear} />
          <div className="flex flex-wrap justify-center gap-2" role="group" aria-label={t("Topik")}>
            {topics.map((x) => (
              <button key={x.id} type="button" aria-pressed={topic === x.id} onClick={() => setTopic(x.id)}
                className={`rounded-full px-5 py-2 text-sm font-extrabold ${topic === x.id ? "bg-violet-500 text-white" : "bg-card text-slate-600 ring-1 ring-black/10"}`}>
                {t(x.label)}
              </button>
            ))}
          </div>
          {error && <p className="text-sm font-semibold text-rose-600">{error}</p>}
        </Intro>
      </>
    );
  }
  if (stage === "end" && final) {
    return <>{header}<Result game={spec.id} score={final.score} max={final.max} details={{ year, topic, ...(final.details ?? {}) }} onAgain={() => setStage("intro")} /></>;
  }
  return <>{header}<Stage key={seed} spec={spec} qs={qs} factory={factory!} mascot={mascot} onHud={setHud} onEnd={(r) => { setFinal(r); setStage("end"); }} onBack={() => setStage("intro")} /></>;
}

function Stage({ spec, qs, factory, mascot, onHud, onEnd, onBack }: {
  spec: ArcadeSpec; qs: ArcadeQ[]; factory: EngineFactory; mascot: "aiman" | "aina";
  onHud: (h: Hud) => void; onEnd: (r: { score: number; max: number; details?: Record<string, string | number | boolean> }) => void; onBack: () => void;
}) {
  const canvas = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);
  const [q, setQ] = useState<ArcadeQ | null>(null);
  const [banner, setBanner] = useState<{ text: string; tone: string; n: number } | null>(null);
  const [rank, setRank] = useState<string>("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const cv = canvas.current; if (!cv) return;
    let bn = 0, timer: ReturnType<typeof setTimeout> | undefined;
    const ctx: EngineCtx = {
      canvas: cv, qs, mascot, tr: t, reduceMotion: reduced(),
      onHud: (h) => { onHud(h); setRank(h.note ?? ""); },
      onQuestion: (x) => {
        setQ(x);
        if (x && spec.speak) { const line = x.say ?? x.text; if (x.lang === "en") speakEn(line); else speak(line); }
      },
      onBanner: (text, tone = "info") => {
        clearTimeout(timer);
        if (!text) { setBanner(null); return; }
        bn++; setBanner({ text, tone, n: bn });
        if (tone === "good" || tone === "bad") timer = setTimeout(() => setBanner(null), 1700);
      },
      onReady: () => setReady(true),
      onError: () => setFailed(true),
      onEnd,
    };
    const eng = factory(ctx);
    return () => { clearTimeout(timer); eng.stop(); };
  }, []);

  if (failed) {
    return (
      <div className="animate-rise rounded-3xl bg-card p-6 text-center shadow-md">
        <p className="font-bold text-slate-800">{t("Peranti ini tidak menyokong grafik 3D untuk permainan ini.")}</p>
        <button type="button" onClick={onBack} className="mt-3 rounded-full bg-violet-500 px-5 py-2 text-sm font-extrabold text-white">{t("Kembali")}</button>
        <Link to="/belajar/permainan" className="ml-2 inline-block rounded-full bg-card px-5 py-2 text-sm font-bold text-slate-700 ring-1 ring-black/10">{t("Permainan lain")}</Link>
      </div>
    );
  }
  const tone = banner?.tone === "good" ? "text-emerald-300" : banner?.tone === "bad" ? "text-rose-200" : "text-white";
  return (
    <div className="mx-auto w-full max-w-md">
      <div className="mb-2 flex items-center gap-2">
        <p className="min-h-[3.25rem] flex-1 rounded-2xl bg-white px-4 py-2 text-center text-xl font-black leading-tight text-slate-900 shadow ring-1 ring-black/5" aria-live="polite">
          {q ? q.text : t("Bersedia…")}
        </p>
        {rank && <span className="rounded-2xl bg-amber-400 px-3 py-2 text-center text-xs font-extrabold leading-tight text-slate-900 shadow">{spec.noteLabel ? t(spec.noteLabel) : t("Tempat")}<br /><span className="text-lg">{rank}</span></span>}
      </div>
      <div className="relative overflow-hidden rounded-3xl shadow-lg ring-1 ring-black/10" style={{ aspectRatio: `${spec.w} / ${spec.h}` }}>
        <canvas ref={canvas} onContextMenu={(e) => e.preventDefault()} aria-label={t("Medan permainan")}
          className="block h-full w-full touch-none select-none" style={{ WebkitTouchCallout: "none", WebkitUserSelect: "none" }} />
        {!ready && <div className="absolute inset-0 grid place-items-center bg-sky-200/80 text-lg font-extrabold text-slate-700">{t("Memuatkan…")}</div>}
        {banner && (
          <div key={banner.n} className="pointer-events-none absolute inset-x-0 top-[18%] grid place-items-center">
            <span className={`animate-rise px-4 text-center font-black drop-shadow-[0_3px_0_rgba(10,16,48,.7)] ${tone} ${banner.tone === "count" ? "text-8xl" : "text-4xl"}`} style={{ WebkitTextStroke: "2px rgba(10,16,48,.55)" }}>{banner.text}</span>
          </div>
        )}
      </div>
      <p className="mt-2 text-center text-xs text-slate-500">{spec.hint ? t(spec.hint) : t("Sentuh kiri, tengah atau kanan untuk tukar lorong.")}</p>
    </div>
  );
}
